import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BuildOutlinedIcon from "@material-ui/icons/BuildOutlined";
import React from "react";

import { Site } from "../../../../../dtos/site";
import { ConsoleCard } from "../console/ConsoleCard";
import { ZoneHead } from "../console/ZoneHead";
import { ActionRow } from "../console/ActionRow";
import { tokens } from "../console/tokens";

import { actionButtonStyle } from "./actionButtonStyle";
import { ChangePasswordButton } from "./ChangePasswordButton";
import { RestartBiometricDeviceButton } from "./RestartBiometricDeviceButton";
import { RestartHmiDeviceButton } from "./RestartHmiDeviceButton";
import { SensorResetButton } from "./SensorResetButton";
import { ServiceRestartButton } from "./ServiceRestartButton";
import { SystemRestartButton } from "./SystemRestartButton";

interface Props {
    site: Site;
    showDangerousButtons: boolean;
    isSiteOnline: boolean;
    onEnableMaintenanceMode: () => void;
}

const useStyles = makeStyles(() => ({
    dangerZone: {
        border: `1px solid ${tokens.dangerLine}`,
        borderRadius: "12px",
        padding: "20px 26px",
        background: tokens.surface,
        boxShadow: tokens.shadow,
    },
    dangerZoneTitle: {
        fontSize: "15.5px",
        fontWeight: 600,
        color: tokens.danger,
        margin: "0 0 3px",
    },
    dangerZoneCaption: {
        fontSize: "13px",
        color: tokens.muted,
        margin: "0 0 20px",
        maxWidth: "62ch",
    },
}));

export function DeviceActionsSection({ site, showDangerousButtons, isSiteOnline, onEnableMaintenanceMode }: Props) {
    const classes = useStyles();

    return (
        <>
            <ZoneHead
                icon={<BuildOutlinedIcon style={{ fontSize: "16px" }} />}
                label="Device actions"
            />

            <ConsoleCard>
                {showDangerousButtons && (
                    <>
                        <ActionRow label="Reset sensors" description="Resets the connected sensors and restarts the HMI and biometric device. Safe to perform at any time.">
                            <SensorResetButton site={site} />
                        </ActionRow>
                        <ActionRow label="Restart biometric device" description="Restarts the biometric device. The device will be temporarily unavailable during the restart.">
                            <RestartBiometricDeviceButton site={site} />
                        </ActionRow>
                        <ActionRow label="Restart HMI device" description="Restarts the HMI device. The screen will be unavailable for approximately 30 seconds.">
                            <RestartHmiDeviceButton site={site} />
                        </ActionRow>
                    </>
                )}
                <ActionRow label="Enable maintenance mode" description="Temporarily suppresses system alerts during scheduled maintenance activities.">
                    <Button color="primary" variant="contained" onClick={onEnableMaintenanceMode} style={actionButtonStyle}>
                        Enable
                    </Button>
                </ActionRow>
            </ConsoleCard>

            {showDangerousButtons && (
                <div className={classes.dangerZone}>
                    <Typography component="h2" className={classes.dangerZoneTitle}>
                        Disruptive — ends any session in progress
                    </Typography>
                    <Typography component="p" className={classes.dangerZoneCaption}>
                        These take the whole site offline for up to two minutes. Confirm no one is mid-access before continuing.
                    </Typography>
                    <ActionRow label="Restart service" description="Restarts the ACS application only. Hardware stays powered.">
                        <ServiceRestartButton site={site} />
                    </ActionRow>
                    <ActionRow label="Restart system" description="Reboots the entire device, including I/O controllers.">
                        <SystemRestartButton site={site} />
                    </ActionRow>
                    <ActionRow label="Change password" description="Changes the device's shared admin password. Requires the site to be online.">
                        <ChangePasswordButton site={site} isSiteOnline={isSiteOnline} />
                    </ActionRow>
                </div>
            )}
        </>
    );
}
